import { getApiUrl } from '../../../config/api';
import { post } from '../../../utils/request-manager';

/**
 * Controlador para la vista de editar parámetro
 * Maneja la lógica de edición de parámetros existentes
 */
export default class EditParametroViewController {
  constructor() {
    this.parametro = {
      ParametroClave: '',
      Valor: '',
      Descripcion: ''
    };
    this.loading = true;
    this.saving = false;
    this.error = null;
    this.parametroId = null;
  }

  /**
   * Inicializa el controlador con el ID del parámetro
   * @param {string} parametroId - ID del parámetro a editar
   */
  initialize(parametroId) {
    this.parametroId = parametroId;
    this.loadParametro();
  }

  /**
   * Carga los datos del parámetro desde la API
   */
  async loadParametro() {
    this.loading = true;
    this.error = null;
    
    try {
      const response = await post(getApiUrl('/api/Parametro/lectura'), {
        ParametroClave: this.parametroId
      });
      
      if (response.data.Data && response.data.Data.length > 0) {
        this.parametro = response.data.Data[0];
        this.loading = false;
      } else {
        this.error = 'Parámetro no encontrado.';
        this.loading = false;
      }
    } catch (error) {
      console.error('Error al cargar parámetro:', error);
      this.error = 'No se pudo cargar el parámetro. Por favor, intente nuevamente.';
      this.loading = false;
    }
  }

  /**
   * Actualiza un campo del parámetro
   * @param {string} field - Nombre del campo
   * @param {any} value - Valor del campo
   */
  updateField(field, value) {
    this.parametro[field] = value;
  }

  /**
   * Guarda los cambios del parámetro
   * @returns {Promise} - Promesa que resuelve con la respuesta de la API
   */
  async saveParametro() {
    this.saving = true;
    this.error = null;
    
    const formData = {
      Parametro: this.parametro
    };
    
    try {
      const response = await post(
        getApiUrl('/api/Parametro/guardar'), 
        formData, 
        {}, 
        false // no usar caché
      );
      
      this.saving = false;
      return response;
    } catch (error) {
      console.error('Error al actualizar parámetro:', error);
      this.error = 'No se pudo actualizar el parámetro. Por favor, verifique los datos e intente nuevamente.';
      this.saving = false;
      throw error;
    }
  }

  /**
   * Valida el formulario
   * @returns {boolean} - True si el formulario es válido
   */
  validateForm() {
    return this.parametro.ParametroClave.trim() !== '' && 
           this.parametro.Valor.trim() !== '';
  }

  /**
   * Obtiene el parámetro actual
   * @returns {Object} - Objeto parámetro
   */
  getParametro() {
    return this.parametro;
  }

  /**
   * Obtiene el estado de carga
   * @returns {boolean} - Estado de carga
   */
  isLoading() {
    return this.loading;
  }

  /**
   * Obtiene el estado de guardado
   * @returns {boolean} - Estado de guardado
   */
  isSaving() {
    return this.saving;
  }

  /**
   * Obtiene el error actual
   * @returns {string|null} - Error actual o null
   */
  getCurrentError() {
    return this.error;
  }

  /**
   * Limpia el error actual
   */
  clearError() {
    this.error = null;
  }

  /**
   * Establece el estado de carga
   * @param {boolean} loading - Estado de carga
   */
  setLoading(loading) {
    this.loading = loading;
  }

  /**
   * Establece el estado de guardado
   * @param {boolean} saving - Estado de guardado
   */
  setSaving(saving) {
    this.saving = saving;
  }

  /**
   * Establece un error
   * @param {string} error - Mensaje de error
   */
  setError(error) {
    this.error = error;
  }

  /**
   * Obtiene el texto del botón de guardar
   * @returns {string} - Texto del botón
   */
  getSaveButtonText() {
    return this.saving ? 'Guardando...' : 'Guardar Cambios';
  }

  /**
   * Verifica si los datos están listos para mostrar
   * @returns {boolean} - True si los datos están listos
   */
  isDataReady() {
    return !this.loading && !this.error;
  }
}